import type {
  SellDirectProductFragment_SellDirectProduct_pricing as CashOfferDetails,
  OdProtosSellCommon_Product,
  SellDirectProductFragment_SellDirectProduct_pricing_PricingsCashOfferDetails as PricingsCashOfferDetails,
  SellDirectProductFragment as SellDirectProduct,
  SellWithUpsideProductFragment as SellWithUpsideProduct,
  SellDirectProductFragment_SellDirectProduct_pricing_UnderwritingCashOfferDetails as UnderwritingCashOfferDetails,
} from '__generated__/graphql';
import { OdProtosSellCommon_Product as Product } from '__generated__/graphql';

export { Product };
export type { SellDirectProduct };

/** Products that are displayed to customers (excludes LIST_WITH_CERTAINTY) */
const AvailableProducts = [Product.OPENDOOR_DIRECT, Product.SELL_WITH_UPSIDE] as const;

export type AvailableProduct = (typeof AvailableProducts)[number];

export const AvailableProductNames: Record<AvailableProduct, string> = {
  [Product.OPENDOOR_DIRECT]: 'Cash offer',
  [Product.SELL_WITH_UPSIDE]: 'Cash now, more later',
};

/**
 * Type guard to check if a product is displayable to customers.
 * Returns true for OPENDOOR_DIRECT and SELL_WITH_UPSIDE only.
 */
export const isAvailableProduct = (p: Product): p is AvailableProduct =>
  (AvailableProducts as readonly Product[]).includes(p);

/** Seller products data with individual product fields and available products list */
export interface SellerProducts {
  /** Displayable product kinds the customer qualified for (Cash and Cash Plus only — Listing isn't displayable) */
  availableProducts: AvailableProduct[];
  /** Product the customer has selected, if any */
  selectedProduct?: AvailableProduct;
  /** Cash offer product data, if customer qualified */
  cashOfferProduct?: SellDirectProduct;
  /** Cash Plus product data, if customer qualified */
  cashPlusProduct?: SellWithUpsideProduct;
  /** Products the seller was previously eligible for but is no longer */
  previouslyEligibleProducts: OdProtosSellCommon_Product[];
}

export const isPricingCashOfferDetails = (
  pricing?: SellDirectProduct['pricing'],
): pricing is PricingsCashOfferDetails => pricing?.['__typename'] === 'PricingsCashOfferDetails';
const isUnderwritingCashOfferDetails = (
  pricing?: SellDirectProduct['pricing'],
): pricing is UnderwritingCashOfferDetails =>
  pricing?.['__typename'] === 'UnderwritingCashOfferDetails';

/**
 * Extracts the pricing from a sell direct product (PricingsCashOfferDetails or UnderwritingCashOfferDetails)
 *
 * @param product a cash offer product to check for offer details
 * @returns CashOfferDetailsFields, if present
 */
export const getPricingsOrUnderwritingCashOfferDetails = (
  product?: SellDirectProduct,
): CashOfferDetails | undefined =>
  isPricingCashOfferDetails(product?.pricing) || isUnderwritingCashOfferDetails(product?.pricing)
    ? product.pricing
    : undefined;
